import { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { LogOut, Menu, Shield, User, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { AppContextProvider, useAppContext } from "@/app/ContextProvider";
import { NAV_ITEMS, type NavItem } from "@/app/nav";
import { statsApi } from "@/api/stats";
import { ContextSwitcher } from "@/components/ContextSwitcher";
import { HighlightsBanner } from "@/components/HighlightsBanner";
import { PointsBreakdownModal } from "@/components/PointsBreakdownModal";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** A saját összpontszám kattintható „chip"-je a fejlécben → saját pontbontás modal. */
function HeaderScore() {
  const { isReady, leagueId, tournamentId } = useAppContext();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["personalStats", leagueId, tournamentId],
    queryFn: () => statsApi.personal({ leagueId: leagueId!, tournamentId: tournamentId! }),
    enabled: isReady,
  });

  if (!isReady || !q.data) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-sm font-semibold text-primary hover:bg-primary/20"
        title="Pontjaid részletezése"
      >
        {q.data.totalPoints} pont
      </button>
      <PointsBreakdownModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function AppLayout() {
  return (
    <AppContextProvider>
      <div className="app-shell min-h-dvh">
        <Header />
        <main className="mx-auto max-w-6xl px-3 py-5 sm:px-4 sm:py-6">
          <HighlightsBanner />
          <Outlet />
        </main>
      </div>
    </AppContextProvider>
  );
}

function Header() {
  const { session, logout, isAdmin } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Route-váltáskor csukódjon be a mobil drawer.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/85 backdrop-blur">
      <div className="mx-auto max-w-6xl px-3 sm:px-4">
        {/* Felső sor */}
        <div className="flex h-14 items-center gap-2">
          <MobileNav open={drawerOpen} onOpenChange={setDrawerOpen} onLogout={logout} fullName={session?.fullName || session?.userName} isAdmin={isAdmin} />

          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <img src="/favicon.png" alt="" className="size-8 rounded-lg" />
            <span className="hidden text-lg font-bold sm:inline">
              Ziccer<span className="text-primary">.</span>
            </span>
          </Link>

          <div className="ml-auto flex items-center gap-1.5">
            <ContextSwitcher className="mr-1 hidden lg:flex" />
            <span className="hidden max-w-[8rem] truncate text-sm text-muted-foreground xl:inline">
              {session?.fullName || session?.userName}
            </span>
            <HeaderScore />
            {isAdmin && (
              <Button asChild variant="ghost" size="icon" className="hidden lg:inline-flex" title="Admin">
                <Link to="/admin" aria-label="Admin"><Shield className="size-4" /></Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="icon" className="hidden lg:inline-flex" title="Profil">
              <Link to="/profil" aria-label="Profil"><User className="size-4" /></Link>
            </Button>
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              aria-label="Kijelentkezés"
              title="Kijelentkezés"
              className="hidden lg:inline-flex"
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>

        {/* Desktop nav-sor */}
        <div className="hidden items-center border-t border-border/40 py-1.5 lg:flex">
          <nav className="flex flex-wrap items-center gap-0.5">
            {NAV_ITEMS.map((item) => (
              <DesktopNavLink key={item.to} item={item} />
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
}

function DesktopNavLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors",
          isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
        )
      }
    >
      <Icon className="size-4" />
      {item.label}
    </NavLink>
  );
}

function MobileNav({
  open,
  onOpenChange,
  onLogout,
  fullName,
  isAdmin,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onLogout: () => void;
  fullName?: string;
  isAdmin: boolean;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Trigger asChild>
        <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Menü">
          <Menu className="size-5" />
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 left-0 z-50 flex w-[85vw] max-w-xs flex-col gap-4 border-r border-border bg-card p-4 shadow-xl focus:outline-none lg:hidden"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between">
            <DialogPrimitive.Title className="flex items-center gap-2 text-lg font-bold">
              <img src="/favicon.png" alt="" className="size-8 rounded-lg" />
              Ziccer<span className="text-primary">.</span>
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Bezárás">
                <X className="size-5" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="rounded-lg border border-border/60 bg-background/50 p-2">
            <ContextSwitcher stack />
          </div>

          <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )
                  }
                >
                  <Icon className="size-5" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="border-t border-border/60 pt-3">
            <div className="mb-2 flex items-center gap-2 px-1 text-sm text-muted-foreground">
              <User className="size-4" />
              <span className="truncate">{fullName}</span>
            </div>
            {isAdmin && (
              <NavLink
                to="/admin"
                className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              >
                <Shield className="size-4" />
                Admin
              </NavLink>
            )}
            <NavLink
              to="/profil"
              className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent/60 hover:text-foreground"
            >
              <User className="size-4" />
              Profil
            </NavLink>
            <Button variant="outline" className="w-full justify-start gap-3" onClick={onLogout}>
              <LogOut className="size-4" />
              Kijelentkezés
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
